import {Component, Inject, OnInit, ChangeDetectionStrategy} from '@angular/core';
import { MAT_DIALOG_DATA, MatDialogRef } from '@angular/material/dialog';
import { CdkDragDrop, moveItemInArray } from '@angular/cdk/drag-drop';
import {FormControl} from "@angular/forms";
import {CachedPipelineDataStore, PipelinesService} from "../../pipelines.service";
import PipelineFieldFilter from "../../../interfaces/pipeline-field-filter";
import {BehaviorSubject, combineLatest,  startWith, Subscription} from "rxjs";
import { LabeledField, PipelineExportService } from "../../pipeline-export.service";
import { AuthService } from "../../../auth/auth.service";
import SavedExportField from "../../../interfaces/saved-export-field";
import { CurrentUserService } from "../../../services/current-user.service";
import PipelineView from "../../../interfaces/pipeline-view";
import ID from "../../../types/id";
import {User} from "../../../interfaces/user";

export interface ExportModalData {
  title?: string
  currentView: PipelineView
  fields: PipelineFieldFilter[]
  dataStore: CachedPipelineDataStore | null
}
@Component({
  selector: 'app-pipelines-export-modal',
  standalone: false,
  templateUrl: './pipelines-export-modal.component.html',
  changeDetection: ChangeDetectionStrategy.Eager,
  styleUrl: './pipelines-export-modal.component.scss',
})
export class PipelinesExportModalComponent implements OnInit{
  modalTitle: string = 'EXPORT PIPELINE - CLOSED';
  presetName: string = '';
  addColumnsOpen: boolean = false;
  searchFieldsText: string = '';
  public presetControl: FormControl<SavedExportField | null> = new FormControl()
  public presetNameControl: FormControl<string> = new FormControl()
  public searchColumnControl: FormControl = new FormControl()


  dataSub: Subscription;
  presetSub:Subscription;
  user: User

  defaultFields: LabeledField[] = [];
  formattedFields: LabeledField[] = [];
  savedExportFields: SavedExportField[] = [];

  selectedAvailableColumns: Set<LabeledField> = new Set();
  selectedFields: LabeledField[] = [];
  selectedFieldsBehavior = new BehaviorSubject(this.selectedFields);
  selectedFields$ = this.selectedFieldsBehavior.asObservable();

  filterSub: Subscription;
  filteredColumns: LabeledField[] = [];
  constructor(
    private dialogRef: MatDialogRef<PipelinesExportModalComponent>,
    @Inject(MAT_DIALOG_DATA) public data: ExportModalData,
    public pipelineService: PipelinesService,
    public exportService: PipelineExportService,
    public authService: CurrentUserService
  ) {

  }

  ngOnInit() {
    if (this.data?.title) {
      this.modalTitle = this.data.title;
    }
    this.dialogRef.disableClose = true;
    let sub = this.authService.user$.subscribe((user) => {
      if(user?.id) {
        this.exportService.getSavedExportFields(user.id);
        this.user = user;
        sub?.unsubscribe();
      }
    })
    let sub2 = combineLatest({
      attributeTypes: this.exportService.attributeTypes$,
      dateTypes: this.exportService.dateTypes$,
      dealParticipants: this.exportService.dealParticipants$,
      fundParticipants: this.exportService.fundParticipants$,
      milestones:this.exportService.milestones$,
    }).subscribe(({attributeTypes, dealParticipants, fundParticipants, milestones, dateTypes})=>{
      if(this.data.currentView &&
        !this.defaultFields?.length &&
        attributeTypes.length &&
        dealParticipants.length &&
        fundParticipants.length &&
        milestones.length &&
        dateTypes.length) {
        this.exportService.getDefaultFields(this.data.currentView, this.data.fields);
        sub2.unsubscribe();
      }
    })
    this.dataSub = combineLatest({
      defaultFields: this.exportService.defaultFields$,
      formattedFields: this.exportService.formattedFields$,
      savedExportFields: this.exportService.savedExportFields$,
    }).subscribe(({defaultFields, formattedFields, savedExportFields})=>{
      if(!this.defaultFields?.length) {
        this.defaultFields = defaultFields
        this.selectedFields = defaultFields
        this.selectedFieldsBehavior.next(this.selectedFields);
      }
      this.formattedFields = formattedFields
      this.savedExportFields = savedExportFields
    });
    this.presetSub = this.presetControl.valueChanges.subscribe((preset: SavedExportField | null) => {
      if(preset){
        let columns:LabeledField[]|string = preset.columns;
        //this is a while loop because sometimes they're double encoded
        while(typeof columns === 'string'){
            columns = JSON.parse(columns as string);
        }
        this.selectedFields = columns as LabeledField[];
        this.selectedFieldsBehavior.next(this.selectedFields);
        this.presetNameControl.setValue(preset.name);
      }else{
        this.selectedFields = this.defaultFields;
        this.selectedFieldsBehavior.next(this.selectedFields);
        this.presetNameControl.setValue('');
      }
    })
    this.filterSub = combineLatest({
      searchValue: this.searchColumnControl.valueChanges.pipe(startWith('')),
      selectedFields: this.selectedFields$,
      formattedFields: this.exportService.formattedFields$
    }).subscribe(({searchValue, selectedFields, formattedFields}) => {
      this.filteredColumns = this.filteredAvailableColumns(searchValue, selectedFields, formattedFields);
    })
  }

  public _fetchColumnList(){
    let attributeColumns: string[]
  }

  filteredAvailableColumns(searchValue:string, selectedFields:LabeledField[], formattedFields: LabeledField[]): LabeledField[] {
    const search = searchValue?.toLowerCase().trim() ?? null;
    let columns = formattedFields;
    if(selectedFields.length !== 0)
      columns = columns.filter((c) =>  !selectedFields.find((f) => f.id === c.id))
    if (!search) return columns;
    return this.filteredColumnsRecursive(columns, search);
  }

  private filteredColumnsRecursive(columns: LabeledField[], search: string):LabeledField[] {
    return columns.reduce((result: LabeledField[], c) => {
      const nameMatches = c.name.toLowerCase().includes(search);
      const filteredChildren = c.children ? this.filteredColumnsRecursive(c.children, search) : [];

      if (nameMatches || filteredChildren.length > 0) {
        result.push({
          ...c,
          children: filteredChildren.length > 0 ? filteredChildren : c.children
        });
      }

      return result;
    }, []);
  }

  // get groupedColumns(): { group: string; items: ExportColumnOption[] }[] {
  //   const groups: { [key: string]: ExportColumnOption[] } = {};
  //   for (const col of this.filteredAvailableColumns) {
  //     if (!groups[col.group]) groups[col.group] = [];
  //     groups[col.group].push(col);
  //   }
  //   return Object.keys(groups).map(g => ({ group: g, items: groups[g] }));
  // }

  toggleChildSelection(index: number, child: LabeledField): void {
    this.selectedFields[index].selectedChildren[child.id] = !this.selectedFields[index].selectedChildren[child.id];
    this.selectedFieldsBehavior.next(this.selectedFields);
  }

  toggleAddColumns(): void {
     this.addColumnsOpen = !this.addColumnsOpen;
     if (!this.addColumnsOpen) {
       this.selectedAvailableColumns.clear();
       this.searchFieldsText = '';
     }
  }

  toggleColumnSelection(field: LabeledField): void {
    if (this.selectedAvailableColumns.has(field)) {
      this.selectedAvailableColumns.delete(field);
    } else {
      this.selectedAvailableColumns.add(field);
    }
  }

  addSelectedColumns(): void {
     for (const field of this.selectedAvailableColumns) {
       if (!this.selectedFields.find(c => c.name === field.name)) {
         this.selectedFields.push(field);
       }
     }
    this.selectedFieldsBehavior.next(this.selectedFields);
     this.selectedAvailableColumns.clear();
     this.addColumnsOpen = false;
     this.searchFieldsText = '';
  }

  cancelSelection(): void {
     this.selectedAvailableColumns.clear();
     this.addColumnsOpen = false;
     this.searchFieldsText = '';
  }

  removeColumn(index: number): void {
    this.selectedFields.splice(index, 1);
  }

  moveColumnUp(index: number): void {
    if (index > 0) {
      moveItemInArray(this.selectedFields, index, index - 1);
    }
  }

  moveColumnDown(index: number): void {
    if (index < this.selectedFields.length - 1) {
      moveItemInArray(this.selectedFields, index, index + 1);
    }
  }

  onDrop(event:any): void {
    moveItemInArray(this.selectedFields, event.previousIndex, event.currentIndex);
  }

  clearAllColumns(): void {
    this.selectedFields = [];
  }

  resetToDefault(): void {
    this.selectedFields = this.defaultFields;
  }

  savePreset(): void {
    if(this.presetControl.value?.id)
      this.exportService.updateSavedExportField({name: this.presetNameControl.value ,columns:JSON.stringify(JSON.stringify(this.selectedFields))}, this.presetControl.value.id)
  }

  saveAsNewPreset(): void {
    if(this.user?.id)
      this.exportService.createSavedExportField({name: this.presetNameControl.value ,columns:JSON.stringify(JSON.stringify(this.selectedFields)), user_id:this.user.id})
  }

  exportPipeline(): void {
    this.exportService.exportPipeline(this.exportService.exportParams(this.data.dataStore, this.selectedFields)).then(()=> {
        this.dialogRef.close();
      }
    )
  }

  closeModal(): void {
    this.dialogRef.close(null);
  }

  ngOnDestroy(): void {
    this.dataSub.unsubscribe();
  }
}
